/* ==========================================================================
   FOOTBALL-THEME :: Filter dock behaviour
   - picture tabs open/close the LEFT side drawers (one at a time); the grid
     stays visible next to them, a backdrop dims the page and closes on tap
   - the bar pins flush: glued under the header, or at the very top once the
     sticky header hides (no floating gap)
   - scrolling the page never closes an open drawer
   - the open drawer survives the section refresh that follows a filter toggle
   - Price drawer: dual-handle range slider + exact number inputs
   - the filtered product count is mirrored into the bar

   Listeners are delegated from `document`, so re-rendered dock HTML keeps
   working without rebinding. The panel state lives on window so the tile
   script can re-apply it after refreshing the section.
   ========================================================================== */

(function () {
  if (window.__fsFdockBoot) return;
  window.__fsFdockBoot = true;

  /* Panels are gesture-driven: the same fsPhysics spring opens them (slide
     in from the left edge), closes them, and backs the swipe below. One
     spring per panel lives in panelSprings; state per panel in __fsOpen.
     Panels seen for the first time (page boot, section re-render after a
     filter change) are restored in place without animating. */
  var panelSprings = new WeakMap();

  function panelSpring(panel) {
    var spring = panelSprings.get(panel);
    if (!spring) {
      spring = window.fsPhysics.spring({
        from: 0,
        to: 0,
        epsilon: 0.5,
        onUpdate: function (value) {
          panel.style.transform = 'translateX(' + value.toFixed(2) + 'px)';
        }
      });
      panelSprings.set(panel, spring);
    }
    return spring;
  }

  function openPanel(panel) {
    panel.style.animation = 'none';
    panel.hidden = false;

    var spring = panelSpring(panel);
    if (window.fsPhysics.reduced()) {
      spring.jump(0);
      panel.style.transform = '';
      return;
    }

    var from = spring.running
      ? spring.value
      : window.fsPhysics.translate(panel, 'x');
    if (from === 0) from = -(panel.offsetWidth || 1); /* fresh open: from the edge */
    spring.jump(from);
    spring.retarget(0, 0, 1, 0.45, function () {
      if (panel.__fsOpen) panel.style.transform = '';
    });
  }

  function closePanel(panel) {
    var spring = panelSpring(panel);
    if (window.fsPhysics.reduced()) {
      panel.hidden = true;
      panel.style.transform = '';
      return;
    }

    var from = spring.running
      ? spring.value
      : window.fsPhysics.translate(panel, 'x');
    spring.jump(from);
    spring.retarget(-(panel.offsetWidth || 1), 0, 1, 0.4, function () {
      if (!panel.__fsOpen) {
        panel.hidden = true;
        panel.style.transform = '';
      }
    });
  }

  function applyState() {
    var openRow = window.__fsFdockOpenRow || null;

    document.querySelectorAll('[data-fs-fdock-panel]').forEach(function (panel) {
      var isOpen = panel.getAttribute('data-fs-fdock-panel') === openRow;

      if (panel.__fsOpen === undefined) {
        /* First sight (boot or section re-render): restore without animating. */
        panel.__fsOpen = isOpen;
        panel.style.animation = 'none';
        panel.hidden = !isOpen;
        if (isOpen) {
          panelSpring(panel).jump(0);
          panel.style.transform = '';
        }
        return;
      }

      if (isOpen === panel.__fsOpen) return;
      panel.__fsOpen = isOpen;

      if (isOpen) {
        openPanel(panel);
      } else {
        closePanel(panel);
      }
    });

    document.querySelectorAll('[data-fs-fdock-backdrop]').forEach(function (backdrop) {
      backdrop.hidden = !openRow;
    });

    document.querySelectorAll('[data-fs-fdock-tab]').forEach(function (tab) {
      var isOpen = tab.getAttribute('data-fs-fdock-tab') === openRow;
      tab.setAttribute('aria-selected', isOpen ? 'true' : 'false');
      tab.classList.toggle('is-active', isOpen);
    });
  }

  function closeDrawer() {
    if (!window.__fsFdockOpenRow) return;
    window.__fsFdockOpenRow = null;
    applyState();
    requestUpdate();
  }

  /* ------------------------------------------------------------------------
     TAG-PATH NAVIGATION (jerseys)
     The jersey tiles are links to /collections/jerseys/<tag>+<tag>. Fetch the
     next page and swap the grid + dock in place so the open drawer stays put.
     Plain navigation remains the fallback when JS/fetch fails.
     ------------------------------------------------------------------------ */
  var lastTagPath = window.location.pathname;

  function setGridLoading(on) {
    var collection = document.querySelector('#ProductGridContainer .collection');
    if (collection) collection.classList.toggle('loading', on);
  }

  function copyProductCount(doc) {
    ['ProductCount', 'ProductCountDesktop'].forEach(function (id) {
      var current = document.getElementById(id);
      var fresh = doc.getElementById(id);
      if (!current || !fresh) return;
      current.textContent = fresh.textContent;
      var dataCount = fresh.getAttribute('data-product-count');
      if (dataCount) current.setAttribute('data-product-count', dataCount);
      var totalCount = fresh.getAttribute('data-total-count');
      if (totalCount) current.setAttribute('data-total-count', totalCount);
    });
  }

  function tagNav(url, replace) {
    var dock = document.querySelector('[data-fs-fdock]');
    var dockSection = dock && dock.closest('.shopify-section');
    var gridWrap = document.getElementById('ProductGridContainer');

    if (!dockSection || !gridWrap || !window.fetch) {
      window.location.href = url;
      return;
    }

    var keyboard = dockSection.contains(document.activeElement);
    var openRow = window.__fsFdockOpenRow;
    setGridLoading(true);

    fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var freshGrid = doc.getElementById('ProductGridContainer');
        var freshDock = doc.getElementById(dockSection.id);
        if (!freshGrid || !freshDock) throw new Error('Sections missing');

        gridWrap.innerHTML = freshGrid.innerHTML;
        dockSection.innerHTML = freshDock.innerHTML;
        copyProductCount(doc);
        if (doc.title) document.title = doc.title;

        var next = new URL(url, window.location.origin);
        lastTagPath = next.pathname;
        if (replace) {
          history.replaceState({ fsTagNav: true }, '', next.href);
        } else {
          history.pushState({ fsTagNav: true }, '', next.href);
        }

        setGridLoading(false);
        window.FS = window.FS || {};
        if (window.FS.applyDockState) window.FS.applyDockState();
        if (window.FS.initTiles) window.FS.initTiles(document);
        if (window.FS.pagerRefresh) window.FS.pagerRefresh();
        mirrorCount();
        initPriceSliders();
        requestUpdate();

        if (keyboard && openRow) {
          var selected = document.querySelector(
            '[data-fs-fdock-panel="' + openRow + '"] [data-fs-tag-nav][aria-current="true"]'
          );
          if (selected) selected.focus({ preventScroll: true });
        }
      })
      .catch(function () {
        setGridLoading(false);
        window.location.href = url;
      });
  }

  document.addEventListener('click', function (event) {
    var tagLink = event.target.closest('[data-fs-tag-nav]');
    if (tagLink && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) {
      event.preventDefault();
      tagNav(tagLink.href, false);
      return;
    }

    var tab = event.target.closest('[data-fs-fdock-tab]');
    if (tab) {
      var row = tab.getAttribute('data-fs-fdock-tab');
      window.__fsFdockOpenRow = window.__fsFdockOpenRow === row ? null : row;
      applyState();
      requestUpdate();
      return;
    }

    if (event.target.closest('[data-fs-fdock-close]') || event.target.closest('[data-fs-fdock-backdrop]')) {
      closeDrawer();
      return;
    }

    var applyPrice = event.target.closest('[data-fs-price-apply]');
    if (applyPrice) {
      var priceRoot = applyPrice.closest('[data-fs-price]');
      if (priceRoot && priceRoot.__fsApply) priceRoot.__fsApply(false);
      return;
    }

    var clearPrice = event.target.closest('[data-fs-price-clear]');
    if (clearPrice) {
      var priceRootClear = clearPrice.closest('[data-fs-price]');
      if (priceRootClear && priceRootClear.__fsApply) priceRootClear.__fsApply(true);
      return;
    }

    if (window.__fsFdockOpenRow && !event.target.closest('[data-fs-fdock]')) {
      closeDrawer();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeDrawer();
  });

  /* Long club / nation lists: narrow the tiles as the customer types. */
  document.addEventListener('input', function (event) {
    var input = event.target.closest('[data-fs-tile-search]');
    if (!input) return;

    var panel = input.closest('[data-fs-fdock-panel]');
    if (!panel) return;

    var query = input.value.trim().toLowerCase();
    var shown = 0;
    panel.querySelectorAll('[data-fs-tile-item]').forEach(function (item) {
      var label = item.getAttribute('data-label') || '';
      var match = !query || label.indexOf(query) !== -1;
      item.hidden = !match;
      if (match) shown++;
    });

    var empty = panel.querySelector('[data-fs-tile-empty]');
    if (empty) empty.hidden = shown !== 0;
  });

  /* Back / forward between tag paths: swap in place. The pager owns
     same-path page changes, so only react when the path actually moves. */
  window.addEventListener('popstate', function () {
    if (window.location.pathname === lastTagPath) return;
    lastTagPath = window.location.pathname;
    tagNav(window.location.href, true);
  });

  /* Count mirror: the filtered count lives on data-product-count (the visible
     text is "1,993 of 5,104 products"). */
  function mirrorCount() {
    var target = document.querySelector('[data-fs-fdock-result]');
    if (!target) return;
    var dock = target.closest('[data-fs-fdock]');
    var noun = (dock && dock.getAttribute('data-fs-fdock-noun')) || 'boots';
    var value = '';
    var count = document.getElementById('ProductCount');
    if (count) {
      var dataCount = parseInt(count.getAttribute('data-product-count') || '', 10);
      if (!isNaN(dataCount)) value = dataCount.toLocaleString();
    }
    if (!value) {
      var nodes = document.querySelectorAll('.product-count__text');
      for (var i = 0; i < nodes.length; i++) {
        var match = nodes[i].textContent.replace(/\s+/g, ' ').match(/([\d,]+)/);
        if (match) {
          value = match[1];
          break;
        }
      }
    }
    if (value) target.textContent = value + ' ' + noun;
  }

  ['ProductCount', 'ProductCountDesktop'].forEach(function (id) {
    var container = document.getElementById(id);
    if (container && window.MutationObserver) {
      new MutationObserver(mirrorCount).observe(container, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
  });

  /* Price slider: dual handles + number inputs, applied through the URL. */
  function initPriceSliders() {
    document.querySelectorAll('[data-fs-price]:not([data-fs-price-ready])').forEach(function (root) {
      root.setAttribute('data-fs-price-ready', '');

      var min = parseInt(root.getAttribute('data-min'), 10) || 0;
      var max = parseInt(root.getAttribute('data-max'), 10) || 0;
      if (max <= min) max = min + 1;

      var gte = parseInt(root.getAttribute('data-gte'), 10);
      var lte = parseInt(root.getAttribute('data-lte'), 10);
      if (isNaN(gte)) gte = min;
      if (isNaN(lte)) lte = max;

      var slider = root.querySelector('[data-fs-price-slider]');
      var inputMin = root.querySelector('[data-fs-price-min]');
      var inputMax = root.querySelector('[data-fs-price-max]');
      if (!slider || !inputMin || !inputMax) return;

      slider.innerHTML =
        '<span class="fs-price__track"></span>' +
        '<span class="fs-price__fill" data-fs-price-fill></span>' +
        '<input type="range" class="fs-price__range" data-fs-price-lo min="' + min + '" max="' + max + '" step="1" value="' + gte + '" aria-label="Minimum price">' +
        '<input type="range" class="fs-price__range" data-fs-price-hi min="' + min + '" max="' + max + '" step="1" value="' + lte + '" aria-label="Maximum price">';

      var lo = slider.querySelector('[data-fs-price-lo]');
      var hi = slider.querySelector('[data-fs-price-hi]');
      var fill = slider.querySelector('[data-fs-price-fill]');

      function paint() {
        var low = parseInt(lo.value, 10);
        var high = parseInt(hi.value, 10);
        if (low > high - 1) {
          if (document.activeElement === lo) {
            low = high - 1;
            lo.value = String(low);
          } else {
            high = low + 1;
            hi.value = String(high);
          }
        }
        var start = ((low - min) / (max - min)) * 100;
        var end = ((high - min) / (max - min)) * 100;
        fill.style.left = start + '%';
        fill.style.width = Math.max(0, end - start) + '%';
        inputMin.value = low <= min ? '' : String(low);
        inputMax.value = high >= max ? '' : String(high);
      }

      lo.addEventListener('input', paint);
      hi.addEventListener('input', paint);
      inputMin.addEventListener('input', function () {
        var value = parseInt(inputMin.value, 10);
        if (!isNaN(value)) {
          lo.value = String(Math.min(Math.max(value, min), max));
          paint();
        }
      });
      inputMax.addEventListener('input', function () {
        var value = parseInt(inputMax.value, 10);
        if (!isNaN(value)) {
          hi.value = String(Math.min(Math.max(value, min), max));
          paint();
        }
      });

      root.__fsApply = function (clear) {
        var params = new URLSearchParams(window.location.search);
        params.delete('filter.v.price.gte');
        params.delete('filter.v.price.lte');
        /* A price change is a new result set - never carry the old page. */
        params.delete('page');
        if (!clear) {
          var low = parseInt(lo.value, 10);
          var high = parseInt(hi.value, 10);
          if (low > min) params.set('filter.v.price.gte', String(low));
          if (high < max) params.set('filter.v.price.lte', String(high));
        }
        var query = params.toString();
        if (typeof FacetFiltersForm !== 'undefined' && typeof FacetFiltersForm.renderPage === 'function') {
          FacetFiltersForm.renderPage(query, null, true);
          if (window.FS && window.FS.refreshSections) {
            window.FS.refreshSections().then(function () {
              if (window.FS.initTiles) window.FS.initTiles(document);
              if (window.FS.afterRefresh) window.FS.afterRefresh();
            });
          }
        } else {
          window.location.search = query;
        }
      };

      paint();
    });
  }

  /* Positioning: when the header slides itself away -> the bar takes the
     very top. Otherwise glue it to the lowest visible header piece (never the
     section wrapper - it stays full height even while the bar floats). */
  function restTop() {
    var host = document.querySelector('.shopify-section.section-football-header sticky-header');
    if (host && host.classList.contains('is-hidden')) return 0;

    var bottom = 0;
    document
      .querySelectorAll(
        '.shopify-section.section-football-offer-bar, ' +
          '.shopify-section.section-football-header sticky-header, ' +
          '.shopify-section.section-football-header .fs-header'
      )
      .forEach(function (el) {
        var rect = el.getBoundingClientRect();
        if (rect.height > 4 && rect.bottom > bottom && rect.bottom < 300) {
          bottom = rect.bottom;
        }
      });
    return Math.max(0, Math.round(bottom));
  }

  var ticking = false;

  function updatePosition() {
    ticking = false;

    var dock = document.querySelector('[data-fs-fdock]');
    if (!dock) return;

    var section = dock.closest('.shopify-section') || dock.parentElement;
    if (!section) return;

    var top = restTop();
    var shouldFix = section.getBoundingClientRect().top < top;

    /* Align the drawers with the real product column (page vars differ
       between the grid and other sections). */
    var anchor = document.querySelector('[data-fs-pager-row="top"]');
    var panels = dock.querySelector('.fs-fdock__panels');
    if (anchor && panels) {
      var anchorLeft = Math.max(0, Math.round(anchor.getBoundingClientRect().left));
      if (panels.__fsLeft !== anchorLeft) {
        panels.__fsLeft = anchorLeft;
        panels.style.left = anchorLeft + 'px';
      }
    }

    /* While glued to the very top (header hidden) sit ABOVE the header's
       leftover sliver so nothing of the tabs hides under it. */
    var host = document.querySelector('.shopify-section.section-football-header sticky-header');
    var headerGone = !!(host && host.classList.contains('is-hidden'));
    dock.classList.toggle('is-topmost', shouldFix && headerGone);

    /* Release with the grid end, like a sticky element leaving its parent. */
    var grid = document.getElementById('ProductGridContainer');
    var gridWrap = grid ? grid.closest('.shopify-section') || grid : null;
    if (gridWrap) {
      var gridBottom = gridWrap.getBoundingClientRect().bottom;
      if (gridBottom - dock.offsetHeight < top) {
        top = Math.max(0, gridBottom - dock.offsetHeight);
      }
    }

    if (shouldFix) {
      dock.classList.add('is-fixed');
      dock.style.top = Math.round(top) + 'px';
      section.style.minHeight = dock.offsetHeight + 'px';
    } else {
      dock.classList.remove('is-fixed');
      dock.style.top = '';
      section.style.minHeight = '';
    }
  }

  function requestUpdate() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(updatePosition);
  }

  /* Scrolling the page never closes the drawer; the bar keeps following
     the header while nothing is open. */
  var settleTimers = [];
  window.addEventListener(
    'scroll',
    function () {
      requestUpdate();
      /* The sticky header animates in/out; keep re-measuring until it has
         fully settled so the bar never parks mid-animation. */
      settleTimers.forEach(clearTimeout);
      settleTimers = [400, 800, 1400].map(function (delay) {
        return setTimeout(requestUpdate, delay);
      });
    },
    { passive: true }
  );

  var stickyHost = document.querySelector('.shopify-section.section-football-header sticky-header');
  if (stickyHost) {
    stickyHost.addEventListener('transitionend', requestUpdate);
  }
  window.addEventListener('resize', requestUpdate);

  document.addEventListener('fs:filters-applied', function () {
    applyState();
    mirrorCount();
    initPriceSliders();
    requestUpdate();
  });

  window.FS = window.FS || {};
  window.FS.applyDockState = applyState;
  window.FS.closeDock = closeDrawer;

  applyState();
  mirrorCount();
  initPriceSliders();
  requestUpdate();
  window.addEventListener('load', function () {
    mirrorCount();
    initPriceSliders();
    requestUpdate();
  });

  /* ------------------------------------------------------------------------
     GRAB THE PANEL: swipe a side drawer left to dismiss.
     1:1 tracking, momentum projection decides the outcome, rubber-band when
     pushed the wrong way, and a running settle can be caught mid-flight
     (the live transform is read as the presentation value). Needs
     assets/football-physics.js. */
  var panelGesture = null;

  function swallowNextClick() {
    var handler = function (event) {
      cleanup();
      event.preventDefault();
      event.stopPropagation();
    };
    function cleanup() {
      window.clearTimeout(timer);
      document.removeEventListener('click', handler, true);
    }
    var timer = window.setTimeout(cleanup, 500);
    document.addEventListener('click', handler, true);
  }

  document.addEventListener('pointerdown', function (event) {
    if (!window.fsPhysics) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    var panel = event.target.closest('[data-fs-fdock-panel]');
    if (!panel || panel.hidden) return;
    if (event.target.closest('input, select, textarea, a, button')) return;

    var spring = panelSprings.get(panel);
    if (spring) spring.stop();

    panelGesture = {
      panel: panel,
      backdrop: document.querySelector('[data-fs-fdock-backdrop]'),
      startX: event.clientX,
      startY: event.clientY,
      startOffset: window.fsPhysics.translate(panel, 'x'),
      width: panel.offsetWidth || 1,
      x: 0,
      engaged: false,
      tracker: window.fsPhysics.tracker()
    };
    panelGesture.tracker.add(event.clientX, performance.now());
  });

  window.addEventListener(
    'pointermove',
    function (event) {
      var g = panelGesture;
      if (!g) return;

      if (!g.engaged) {
        var mx = event.clientX - g.startX;
        var my = event.clientY - g.startY;
        if (Math.abs(mx) < 8 && Math.abs(my) < 8) return;
        if (Math.abs(my) >= Math.abs(mx)) {
          panelGesture = null; /* vertical: let the panel scroll */
          return;
        }
        g.engaged = true;
        g.panel.classList.add('is-dragging');
        g.panel.style.animation = 'none';
        /* Seize from the live (presentation) value; restart from here. */
        g.startOffset = window.fsPhysics.translate(g.panel, 'x');
        g.startX = event.clientX;
        g.startY = event.clientY;
        g.tracker.reset();
      }

      g.tracker.add(event.clientX, performance.now());

      var dx = event.clientX - g.startX;
      var x = g.startOffset + dx;
      var width = g.width;
      if (x > 0) {
        x = window.fsPhysics.rubberband(x, width);
      } else if (x < -width) {
        x = -width - window.fsPhysics.rubberband(-width - x, width);
      }

      g.x = x;
      g.panel.style.transform = 'translateX(' + x.toFixed(2) + 'px)';
      if (g.backdrop) {
        g.backdrop.style.opacity = String(window.fsPhysics.clamp(1 + x / width, 0, 1));
      }
    },
    { passive: true }
  );

  function endPanelGesture() {
    var g = panelGesture;
    if (!g) return;
    panelGesture = null;
    if (!g.engaged) return;

    g.panel.classList.remove('is-dragging');
    var velocity = g.tracker.velocity(performance.now());
    var projected = g.x + window.fsPhysics.project(velocity);
    /* Velocity sign decides reverse vs commit; position only when parked. */
    var close;
    if (velocity < -250) {
      close = true; /* flicked toward dismiss */
    } else if (velocity > 250) {
      close = false; /* moving back toward open */
    } else {
      close = projected < -g.width * 0.5; /* parked: judge by where it would rest */
    }

    swallowNextClick();

    var finish = function (closed) {
      if (g.backdrop) g.backdrop.style.opacity = '';
      if (closed) {
        /* closeDrawer() -> applyState() -> closePanel() owns the transform
           and hides the panel at the off-screen position. */
        closeDrawer();
      } else {
        g.panel.style.transform = '';
        g.panel.style.animation = '';
      }
    };

    if (window.fsPhysics.reduced()) {
      finish(close);
      return;
    }

    var spring = panelSprings.get(g.panel);
    if (!spring) {
      spring = window.fsPhysics.spring({
        from: g.x,
        to: g.x,
        epsilon: 0.2,
        onUpdate: function (value) {
          g.panel.style.transform = 'translateX(' + value.toFixed(2) + 'px)';
        }
      });
      panelSprings.set(g.panel, spring);
    } else {
      spring.jump(g.x);
    }

    spring.retarget(
      close ? -g.width : 0,
      velocity,
      close ? 0.9 : 1,
      close ? 0.35 : 0.45,
      function () {
        finish(close);
      }
    );
  }

  window.addEventListener('pointerup', endPanelGesture);
  window.addEventListener('pointercancel', endPanelGesture);
})();
